import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Calculator, RotateCcw, Sparkles, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";

const defaultsFromFactors = (factors) =>
  Object.fromEntries((factors || []).map((f) => [f.key, String(f.default)]));

export function EstimatorDialog({ metric, baseYear, open, onClose, onApplied }) {
  const [methods, setMethods] = useState(null);
  const [methodId, setMethodId] = useState(null);
  const [inputs, setInputs] = useState({});
  const [factors, setFactors] = useState({});
  const [preview, setPreview] = useState(null);
  const [calcBusy, setCalcBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);

  const method = (methods || []).find((m) => m.id === methodId) || null;

  useEffect(() => {
    if (!open || !metric) return;
    setMethods(null); setMethodId(null); setInputs({}); setFactors({}); setPreview(null);
    api.get("/baseline/estimate/methods", {
      params: { category: metric.category, activity_type: metric.activity_type, unit: metric.unit, base_year: baseYear },
    }).then((r) => {
      const ms = r.data.methods || [];
      setMethods(ms);
      if (ms.length) {
        setMethodId(ms[0].id);
        setFactors(defaultsFromFactors(ms[0].factors));
      }
    }).catch((e) => toast.error(apiErr(e)));
  }, [open, metric, baseYear]);

  const onMethodChange = (id) => {
    const m = (methods || []).find((x) => x.id === id);
    setMethodId(id);
    setInputs({});
    setFactors(defaultsFromFactors(m?.factors));
    setPreview(null);
  };

  const setInput = (k, v) => { setInputs((s) => ({ ...s, [k]: v })); setPreview(null); };
  const setFactor = (k, v) => { setFactors((s) => ({ ...s, [k]: v })); setPreview(null); };
  const resetFactors = () => { setFactors(defaultsFromFactors(method?.factors)); setPreview(null); };

  const calculate = useCallback(async () => {
    if (!methodId) return;
    setCalcBusy(true);
    try {
      const r = await api.post("/baseline/estimate/preview", { method_id: methodId, inputs, factors });
      setPreview(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCalcBusy(false); }
  }, [methodId, inputs, factors]);

  const apply = async () => {
    if (!methodId) return;
    setApplyBusy(true);
    try {
      const r = await api.post("/baseline/estimate/apply", {
        pillar: metric.pillar, category: metric.category, activity_type: metric.activity_type,
        unit: metric.unit, base_year: Number(baseYear), method_id: methodId, inputs, factors,
      });
      toast.success(`Baseline estimated — ${formatNum(r.data.value)} ${r.data.output_unit}`);
      onApplied && onApplied(r.data.value);
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setApplyBusy(false); }
  };

  const hasInput = Object.values(inputs).some((v) => v !== "" && v != null && Number(v) > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="estimator-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Calculator className="h-5 w-5 text-accent" /> Estimate baseline — {metric?.activity_type}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">
          Enter the operational data you actually have for <span className="text-foreground font-medium">FY{baseYear}</span> and we'll calculate the baseline using documented factors. Saving records the inputs, factors and source as audit evidence.
        </p>

        {!methods ? (
          <div className="py-10 text-center text-muted-foreground text-sm">Loading methods…</div>
        ) : !methods.length ? (
          <div className="py-8 text-center text-muted-foreground text-sm">No calculator available for this metric.</div>
        ) : (
          <div className="space-y-4">
            {methods.length > 1 && (
              <div>
                <div className="label-eyebrow text-[10px] mb-1">Calculation method</div>
                <Select value={methodId} onValueChange={onMethodChange}>
                  <SelectTrigger data-testid="estimator-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {method && (
              <div className="flex items-start gap-2 rounded-sm border border-border bg-surface p-2.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" /> {method.description}
              </div>
            )}

            {/* Operational inputs */}
            <div>
              <div className="label-eyebrow text-[10px] mb-2">Operational data (enter what you have)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(method?.inputs || []).map((inp) => (
                  <div key={inp.key}>
                    <div className="text-xs mb-1">{inp.label} <span className="text-muted-foreground">({inp.unit})</span></div>
                    <Input type="number" step="any" value={inputs[inp.key] ?? ""}
                      onChange={(e) => setInput(inp.key, e.target.value)}
                      placeholder="0" data-testid={`estimator-input-${inp.key}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Editable factors */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow text-[10px]">Conversion / emission factors</div>
                <button onClick={resetFactors} className="text-[10px] text-accent hover:underline inline-flex items-center gap-1" data-testid="estimator-reset-factors">
                  <RotateCcw className="h-3 w-3" /> Reset to defaults
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(method?.factors || []).map((fa) => (
                  <div key={fa.key}>
                    <div className="text-xs mb-1">{fa.label} <span className="text-muted-foreground">({fa.unit})</span></div>
                    <Input type="number" step="any" value={factors[fa.key] ?? ""}
                      onChange={(e) => setFactor(fa.key, e.target.value)}
                      data-testid={`estimator-factor-${fa.key}`} />
                    <div className="text-[9px] text-muted-foreground mt-0.5">Source: {fa.source}</div>
                  </div>
                ))}
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={calculate} disabled={calcBusy || !hasInput} data-testid="estimator-calculate">
              <Calculator className="h-4 w-4" /> {calcBusy ? "Calculating…" : "Calculate"}
            </Button>

            {/* Result */}
            {preview && (
              <div className="card-flat p-4 border-accent/30" data-testid="estimator-result">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> Calculated baseline</div>
                  <Badge variant="outline" className="text-[9px]">FY{baseYear}</Badge>
                </div>
                <div className="font-display text-3xl font-bold mt-1" data-testid="estimator-result-value">
                  {formatNum(preview.value)} <span className="text-base text-muted-foreground font-sans">{preview.output_unit}</span>
                  {preview.output_unit !== "tCO2e" && preview.co2e > 0 && (
                    <span className="text-sm text-muted-foreground ml-2">· ~{formatNum(preview.co2e, { decimals: 2 })} tCO₂e</span>
                  )}
                </div>
                <div className="mt-3 space-y-1">
                  {preview.breakdown.map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border-b border-border/50 pb-1">
                      <span className="text-muted-foreground">{b.label} — <span className="font-mono">{b.detail}</span></span>
                      {b.co2e_kg > 0 && <span className="font-mono">{formatNum(b.co2e_kg / 1000, { decimals: 3 })} tCO₂e</span>}
                    </div>
                  ))}
                  {!preview.breakdown.length && <div className="text-xs text-muted-foreground">Enter at least one operational value above.</div>}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} data-testid="estimator-cancel">Cancel</Button>
              <Button variant="primary" onClick={apply} disabled={applyBusy || !preview} data-testid="estimator-apply">
                {applyBusy ? "Applying…" : "Apply as baseline"} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
